import DropIn from 'braintree-web-drop-in-react';
import { FC, useEffect, useState } from 'react';
import { ButtonSubmit, ButtonSubmitType } from "components/button/ButtonSubmit";
import ReactGA from 'react-ga';
import './style/style.scss'
import { useBuyQuery } from './hooks/useBuyQuery';
import { useBraintreeValidate } from 'index';
import { useDebounce, useRuntimeEnv } from 'hooks';

/**
 *
 * Component displaying Braintree payment form to perform 
 * payment. Uses DropIn component from braintree-web-drop-in-react
 *
 */
export const BraintreeForm:FC<Props> = ({clientToken, totalAmount, cartToken, onPaymentSuccess, onPaymentError, ...props} :Props) => {

    // DropIn generated instance
    const [instance, setInstance] = useState<any>() ;
    
    const [buying, setBuying] = useState<boolean>() ;
    
    // Pay button disabling
    const [disabled, setDisabled] = useState<boolean>(true) ;
    const env = useRuntimeEnv()
    const mutation = useBuyQuery()
    const validateMutation = useBraintreeValidate()
    const debounce = useDebounce()

    useEffect(() => {
        mutation.isSuccess && checkValidation()
    }, [mutation.isSuccess])

    useEffect(() => {

        if (validateMutation.isSuccess && validateMutation.data?.orderStatus) {
            if (['DELIVERING', 'DELIVERED', 'VALIDATED'].includes(validateMutation.data.orderStatus)) {
                onPaymentSuccess?.();
                ga_PaySuccess();
            } else if (validateMutation.data.orderStatus === 'FAILURE') {
                props.onPaymentFailed?.();
            } else {
                validateMutation.reset()
                debounce(() => checkValidation(), 2500)
            }
        }

    }, [validateMutation.isSuccess])

    useEffect(() => {
        if (mutation.isError) {
            onPaymentError?.(mutation.error as any)
            ga_PayError();
        }
    }, [mutation.isError])
            
    useEffect(() => {

        // Enable pay button when form ready
        if(instance){        
            instance.on('paymentMethodRequestable', () =>  {
                setDisabled(!instance.isPaymentMethodRequestable());
            });
        }

        if(instance && buying){
            if (props.threeDSecure) {
                return instance.requestPaymentMethod({ threeDSecure: props.threeDSecureParameters }, handlePaymentChange);
            }
            instance.requestPaymentMethod(handlePaymentChange);
        }
    },[instance, buying, cartToken, totalAmount]) ;

    const checkValidation = () => {
        if (mutation.data?.customerorderId) {
            validateMutation.mutate({
                customerorderId: mutation.data?.customerorderId,
                lg: env?.language
            })
        }
    }

    const handlePaymentChange = (err:Error, payload:any) => {
        console.log(err, payload)
        if(err){
            onPaymentError?.(err) ;
            ga_PayError();
            setDisabled(false)
        } else {
            props.onPayment?.()
            ga_Pay();
            mutation.mutate({
                braintreeNonce: payload.nonce,
                deviceData: payload.deviceData,
                cartToken,
                totalAmount
            })
        }
    }

    const getPaypalData = () => {
        return Boolean(process.env.REACT_APP_PAYMENT_PAYPAL)
        ? {
            flow: 'checkout',
            amount: totalAmount,
            currency: props.currency
        }
        : undefined
    }

    return (
        <>
        {clientToken &&
            <div onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
            }}> 
                <DropIn
                    options={{
                        authorization: clientToken,
                        paypal: getPaypalData(),
                        locale: navigator.language,
                        dataCollector: props.threeDSecure,
                        threeDSecure: props.threeDSecure
                    }}
                    onInstance={(i) => setInstance(i)}
                />
                <div className="d-flex">
                    <ButtonSubmit 
                        label="PAY..."
                        {...props.buttonSubmit} 
                        disabled={disabled}
                        isSubmit={buying}
                        onClick={() => setBuying(true)} />
                </div>
                
            </div>             
        }</>
    )
}



type Props = {
 
    /** Braintree token needed to display drop in payment form supplied by Braintree*/
    clientToken : string | undefined,
    
    /** Total amount (required by backend controller) */
    totalAmount : number,

    currency?: string,

    /** Total amount (required by backend controller) */
    cartToken : string,

    /** Callback when payment is about to be performed */
    onPayment?: () => void
    
    /** Callback on payment success */
    onPaymentSuccess?: () => void  

    /** Callback on payment error */
    onPaymentError?: (error: Error) => void

    /** Callback on payment successed but the customerorder turnet to "FAILED"  */
    onPaymentFailed?: () => void
    /** Button submit props */
    buttonSubmit?: ButtonSubmitType
    /** 
     * Implement 3DSecure for braintree.
     * Please, visit https://developer.paypal.com/braintree/docs/guides/3d-secure/client-side
     */
    threeDSecure?: boolean
    /**
     * To use 3DS, you will need to pass an object with relevant customer and 
     * transaction data into the threeDSecure field of the requestPaymentMethod 
     * options in order to minimize the need for issuing banks to present authentication challenges to customers. 
     * This object must contain the amount field and should contain as many of the following fields as possible.
     * Please, visit https://developer.paypal.com/braintree/docs/guides/3d-secure/client-side#using-3ds
     */
    threeDSecureParameters?: ThreeDSecureParameters
}

export type ThreeDSecureParameters = {
    amount: number
    email?: string
    billingAddress: {
        /** ASCII-printable characters required, else will throw a validation error */
        givenName?: string
        /** ASCII-printable characters required, else will throw a validation error */
        surname?: string
        phoneNumber?: string
        streetAddress?: string
        extendedAddress?: string
        locality?: string
        region?:  string
        postalCode?: string
        countryCodeAlpha2?: string
    },
    additionalInformation?: {
      workPhoneNumber?: string
      shippingGivenName?: string
      shippingSurname?: string
      shippingPhone?: string
      shippingAddress?: {
        streetAddress?: string
        extendedAddress?: string
        locality?: string
        region?: string
        postalCode?: string
        countryCodeAlpha2?: string
      }
    },
  }

const ga_PaySuccess = () => {
    ReactGA.event({
      category: 'Event',
      action: 'Pay',
      label: 'Event/PaySuccess/PayementSuccessful',
    });
  }

  const ga_PayError = () => {
    ReactGA.event({
      category: 'Event',
      action: 'Pay',
      label: 'Event/PayError/PayementError',
    });
  }

  const ga_Pay = () => {
    ReactGA.event({
      category: 'Event',
      action: 'Pay',
      label: 'Event/PayEvent/PayementProcedureInitiated',
    });
  }